/**
 * IMPORTACIÓN: UN FLUJO, NO CINCO PÁGINAS
 *
 *   1. Elegir el fichero → 2. Errores por fila → 3. Preview de cambios →
 *   4. Confirmar → 5. Publicado
 *
 * Todo en una pantalla con pasos, porque es un solo trabajo: cinco URL
 * distintas obligarían a llevar el estado a mano y a poder llegar al paso
 * 4 sin haber pasado por el 3.
 *
 * LA UI NO DECIDE SI SE PUEDE CONFIRMAR
 *
 * `confirmable` viene del preview del backend. Y las confirmaciones
 * materiales que se marcan aquí las vuelve a comprobar el Commit contra su
 * propio preview recalculado: si el usuario marca de menos o de más, falla
 * allí. Esta pantalla presenta y recoge; no relaja nada.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { api, PLANTILLA_PLAN, type Importacion, type UbicacionImportacion } from '../api.ts';
import { guardarFichero } from './exportaciones.tsx';
import { Cargando, Error as ErrorCaja, fecha, hoyLocal, useDatos } from '../componentes/base.tsx';
import { useCentro } from './centro.tsx';

// ------------------------------------------------------------ contratos

interface Cambio {
  campo: string;
  anterior: string | null;
  nuevo: string | null;
  severidad: 'MATERIAL' | 'DESTACADO' | 'NORMAL';
  anteriorTexto?: string | null;
  nuevoTexto?: string | null;
}

interface TareaPreview {
  referenciaTarea: string;
  clasificacion: string;
  numeroFila: number | null;
  descripcionTarea: string | null;
  codigoUbicacion: string | null;
  codigoTipoLimpieza: string | null;
  frecuenciaEtiqueta: string | null;
  fueraContrato: boolean | null;
  cambios: Cambio[];
  /** El backend los manda con `detalle` (contrato de preview/tipos.ts); `mensaje` no existe. */
  avisos: { codigo: string; detalle: string; bloqueante?: boolean }[];
  requiereConfirmacionExplicita: boolean;
}

interface Preview {
  idImportacion: string;
  idPlanBase: string | null;
  codigoPlan: string;
  confirmable: boolean;
  totales: {
    candidatas: number;
    filasError: number;
    anadidas: number;
    retiradas: number;
    modificadas: number;
    sinCambios: number;
    conservadasManuales: number;
    cambiosMateriales: number;
    cambiosFueraContrato: number;
  };
  tareas: TareaPreview[];
  filasConError: {
    numeroFila: number;
    referenciaTarea: string | null;
    errores: { codigo: string; campo: string | null; detalle: string }[];
  }[];
  avisos: { codigo: string; detalle: string; bloqueante: boolean; referencias?: string[] }[];
  /** Plantilla v2: la hoja «Ubicaciones» (ausente si el fichero no la trae). */
  ubicaciones?: {
    totales: { existentes: number; aCrear: number; conflictos: number; errores: number };
    filas: UbicacionImportacion[];
  };
}

interface ResultadoConfirmacion {
  commit: {
    idPlanGenerado: string;
    codigoPlan: string;
    numeroVersion: number;
    totalLineas: number;
    anadidas: number;
    modificadas: number;
    retiradas: number;
    sinCambios: number;
    conservadasManuales: number;
    defaultsAsignacionCopiados: number;
    defaultsAsignacionOmitidos: number;
    documentosAplicablesCopiados: number;
    /** Plantilla v2: ubicaciones creadas en la misma confirmación. */
    ubicacionesCreadas?: number;
  };
  impacto: {
    otsEvaluadas: number;
    otsImpactadas: number;
    otsImpactadasCerradas: number;
    otsImpactadasAnuladas: number;
    tareasNuevasNoIncluidas: number;
    ocurrenciasQueYaNoSeProducirian: number;
    otsNuevasRequeridas: number;
    conflictosGenerador: number;
  } | null;
  impactoError: string | null;
}

type Paso = 'fichero' | 'revision' | 'confirmando' | 'publicado';

// ------------------------------------------------------------- pantalla

export function PaginaNuevaImportacion(): ReactNode {
  const { centro } = useCentro();
  const navegar = useNavigate();

  const [paso, setPaso] = useState<Paso>('fichero');
  const [codigoPlan, setCodigoPlan] = useState('');
  const [fichero, setFichero] = useState<File | null>(null);
  const [importacion, setImportacion] = useState<Importacion | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [vigenteDesde, setVigenteDesde] = useState(manana());
  //  Sin valor por defecto: sólo se exige (y se envía) cuando el plan es
  //  nuevo, y no hay ninguna hora "razonable" que inventar por él.
  const [horaProgramada, setHoraProgramada] = useState('');
  const [materialesAceptadas, setMaterialesAceptadas] = useState<Set<string>>(new Set());
  const [resultado, setResultado] = useState<ResultadoConfirmacion | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);

  const materialesPendientes = useMemo(
    () => (preview?.tareas ?? []).filter((t) => t.requiereConfirmacionExplicita),
    [preview],
  );
  const todasMarcadas = materialesPendientes.every((t) =>
    materialesAceptadas.has(t.referenciaTarea),
  );

  async function subir(): Promise<void> {
    if (fichero === null || codigoPlan.trim() === '') return;
    setOcupado(true);
    setError(null);
    try {
      const datos = new FormData();
      datos.append('codigoPlan', codigoPlan.trim());
      datos.append('fichero', fichero);
      const imp = await api.subir<{ idImportacion: string }>(
        `/api/centros/${centro.id}/importaciones`,
        datos,
      );
      const detalle = await api.get<Importacion>(`/api/importaciones/${imp.idImportacion}`);
      setImportacion(detalle);

      //  Una importación FALLIDA no llega a tener preview: el lote entero
      //  se rechazó. Se enseña el motivo y se para aquí.
      if (detalle.estado !== 'FALLIDA') {
        setPreview(await api.get<Preview>(`/api/importaciones/${imp.idImportacion}/preview`));
      }
      setPaso('revision');
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }

  async function confirmar(): Promise<void> {
    if (importacion === null) return;
    setOcupado(true);
    setError(null);
    setPaso('confirmando');
    try {
      const r = await api.post<ResultadoConfirmacion>(
        `/api/importaciones/${importacion.id}/confirmar`,
        {
          vigenteDesde,
          confirmacionesMateriales: [...materialesAceptadas],
          //  Sólo va en el cuerpo cuando está informada: en una
          //  reimportación sobre un plan existente el backend hereda la
          //  hora de la versión base, y no hay que pisarla con nada.
          ...(horaProgramada.trim() !== '' ? { horaProgramada } : {}),
        },
      );
      setResultado(r);
      setPaso('publicado');
    } catch (e) {
      setError(e);
      setPaso('revision');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <>
      <Pasos actual={paso} />

      {error !== null && <ErrorCaja error={error} />}

      {paso === 'fichero' && (
        <div className="panel">
          <div className="panel-cabecera">
            <h2>Nueva importación</h2>
          </div>
          <div className="panel-cuerpo">
            <p className="suave">
              Sube la plantilla XLSX. No se publica nada al subirla: primero se valida fila a fila y
              se enseña exactamente qué cambiaría respecto al plan vigente.
            </p>

            <DescargarPlantilla idCentro={centro.id} />

            <div className="campo" style={{ maxWidth: 320 }}>
              <label htmlFor="codigo-plan">Código del plan destino</label>
              <input
                id="codigo-plan"
                type="text"
                placeholder="PMB-NORTE"
                value={codigoPlan}
                onChange={(e) => setCodigoPlan(e.target.value.toUpperCase())}
              />
              <div className="pequena tenue" style={{ marginTop: 4 }}>
                Si ya existe, se comparará contra su versión vigente. Si no, será un plan nuevo.
              </div>
            </div>

            <div className="campo" style={{ maxWidth: 420 }}>
              <label htmlFor="fichero">Fichero XLSX</label>
              <input
                id="fichero"
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => setFichero(e.target.files?.[0] ?? null)}
              />
            </div>

            <button
              className="principal"
              disabled={ocupado || fichero === null || codigoPlan.trim() === ''}
              onClick={() => {
                void subir();
              }}
            >
              {ocupado ? 'Subiendo y validando…' : 'Subir y validar'}
            </button>
          </div>
        </div>
      )}

      {paso === 'confirmando' && <Cargando texto="Publicando la versión…" />}

      {paso === 'revision' && importacion !== null && (
        <Revision
          importacion={importacion}
          preview={preview}
          vigenteDesde={vigenteDesde}
          alCambiarVigencia={setVigenteDesde}
          horaProgramada={horaProgramada}
          alCambiarHora={setHoraProgramada}
          materialesAceptadas={materialesAceptadas}
          alAlternarMaterial={(ref) =>
            setMaterialesAceptadas((s) => {
              const n = new Set(s);
              if (n.has(ref)) n.delete(ref);
              else n.add(ref);
              return n;
            })
          }
          todasMarcadas={todasMarcadas}
          ocupado={ocupado}
          alConfirmar={() => {
            void confirmar();
          }}
        />
      )}

      {paso === 'publicado' && resultado !== null && (
        <Publicado
          resultado={resultado}
          alVerPlan={() => navegar(`/centros/${centro.id}/planificacion`)}
          alGenerar={() => navegar(`/centros/${centro.id}/ots?generar=1`)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------- pasos

const ORDEN: Paso[] = ['fichero', 'revision', 'confirmando', 'publicado'];
const ETIQUETAS: Record<Paso, string> = {
  fichero: 'Fichero',
  revision: 'Revisar cambios',
  confirmando: 'Publicar',
  publicado: 'Publicado',
};

function Pasos({ actual }: { actual: Paso }): ReactNode {
  const i = ORDEN.indexOf(actual);
  return (
    <div className="pasos">
      {ORDEN.filter((p) => p !== 'confirmando').map((p, n) => {
        const j = ORDEN.indexOf(p);
        const clase = j === i ? 'activo' : j < i ? 'hecho' : '';
        return (
          <div key={p} className={`paso ${clase}`}>
            <span className="n">{j < i ? '✓' : n + 1}</span>
            {ETIQUETAS[p]}
          </div>
        );
      })}
    </div>
  );
}

// -------------------------------------------------------------- revisión

function Revision({
  importacion,
  preview,
  vigenteDesde,
  alCambiarVigencia,
  horaProgramada,
  alCambiarHora,
  materialesAceptadas,
  alAlternarMaterial,
  todasMarcadas,
  ocupado,
  alConfirmar,
}: {
  importacion: Importacion;
  preview: Preview | null;
  vigenteDesde: string;
  alCambiarVigencia: (v: string) => void;
  horaProgramada: string;
  alCambiarHora: (v: string) => void;
  materialesAceptadas: ReadonlySet<string>;
  alAlternarMaterial: (ref: string) => void;
  todasMarcadas: boolean;
  ocupado: boolean;
  alConfirmar: () => void;
}): ReactNode {
  if (importacion.estado === 'FALLIDA') {
    return (
      <div className="panel">
        <div className="panel-cuerpo">
          <div className="nota error">
            <strong>El fichero no se ha podido procesar.</strong>
            <div style={{ marginTop: 4 }}>
              {importacion.errorDetalle ?? importacion.errorCodigo ?? 'El lote entero se ha rechazado.'}
            </div>
            {importacion.errorDetalle !== null && importacion.errorDetalle !== undefined && importacion.errorCodigo !== null && (
              <div className="pequena tenue mono" style={{ marginTop: 4 }}>{importacion.errorCodigo}</div>
            )}
          </div>
          <TablaErroresLote importacion={importacion} />
        </div>
      </div>
    );
  }

  if (preview === null) return <Cargando />;

  const t = preview.totales;
  const bloqueantes = preview.avisos;

  return (
    <>
      {preview.ubicaciones !== undefined && <UbicacionesLote ubicaciones={preview.ubicaciones} />}

      <div className="panel">
        <div className="panel-cabecera">
          <h2>Qué cambiaría</h2>
          <span className="suave pequena mono">{preview.codigoPlan}</span>
        </div>
        <div className="panel-cuerpo">
          <div className="cifras">
            <Cifra n={t.anadidas} t="Añadidas" />
            <Cifra n={t.modificadas} t="Modificadas" />
            <Cifra n={t.retiradas} t="Retiradas" />
            <Cifra n={t.sinCambios} t="Sin cambios" />
            <Cifra n={t.conservadasManuales} t="Manuales conservadas" />
            <Cifra n={t.cambiosMateriales} t="Cambios materiales" />
            <Cifra n={t.cambiosFueraContrato} t="Cambian de contrato" />
            <Cifra n={t.filasError} t="Filas con error" />
          </div>
        </div>
      </div>

      {bloqueantes.length > 0 && (
        <div className="nota aviso">
          <strong>Avisos del lote</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {bloqueantes.map((a, i) => (
              <li key={i}>{a.detalle}</li>
            ))}
          </ul>
        </div>
      )}

      {preview.filasConError.length > 0 && (
        <div className="panel">
          <div className="panel-cabecera">
            <h2>Filas con error</h2>
            <span className="suave pequena">no se importarán</span>
          </div>
          <div className="panel-cuerpo sin-relleno">
            <div className="tabla-desliza">
              <table>
                <thead>
                  <tr>
                    <th className="num">Fila</th>
                    <th>Referencia</th>
                    <th>Qué pasa</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.filasConError.map((f) => (
                    <tr key={f.numeroFila}>
                      <td className="num">{f.numeroFila}</td>
                      <td className="cod">{f.referenciaTarea ?? '—'}</td>
                      <td>
                        {f.errores.map((e, i) => (
                          <div key={i}>
                            {e.detalle}
                            {e.campo !== null && <span className="tenue pequena"> ({e.campo})</span>}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <TablaTareasPreview
        tareas={preview.tareas}
        materialesAceptadas={materialesAceptadas}
        alAlternarMaterial={alAlternarMaterial}
      />

      <div className="panel">
        <div className="panel-cabecera">
          <h2>Publicar</h2>
        </div>
        <div className="panel-cuerpo">
          {!preview.confirmable && (
            <div className="nota error">
              Esta importación no se puede confirmar tal cual. Revisa los avisos y las filas con
              error.
            </div>
          )}
          {!todasMarcadas && (
            <div className="nota aviso">
              Hay cambios <strong>materiales</strong> —de ubicación o de tipo— sin aceptar. Un cambio
              material altera dónde o de qué clase es el trabajo: hay que confirmarlo uno a uno.
            </div>
          )}

          <div className="campo" style={{ maxWidth: 220 }}>
            <label htmlFor="vigente-desde">Vigente desde</label>
            <input
              id="vigente-desde"
              type="date"
              value={vigenteDesde}
              onChange={(e) => alCambiarVigencia(e.target.value)}
            />
          </div>

          {preview.idPlanBase === null && (
            <div className="campo" style={{ maxWidth: 220 }}>
              <label htmlFor="hora-programada">Hora programada</label>
              <input
                id="hora-programada"
                type="time"
                value={horaProgramada}
                onChange={(e) => alCambiarHora(e.target.value)}
              />
              <div className="pequena tenue" style={{ marginTop: 4 }}>
                Es la primera versión de este plan: no hay una hora anterior de la que heredarla.
              </div>
            </div>
          )}

          <button
            className="principal"
            disabled={
              ocupado ||
              !preview.confirmable ||
              !todasMarcadas ||
              (preview.idPlanBase === null && horaProgramada.trim() === '')
            }
            onClick={alConfirmar}
          >
            Publicar nueva versión
          </button>
        </div>
      </div>
    </>
  );
}

/** Descarga de la plantilla oficial, con los tipos de limpieza y las ubicaciones de ESTE centro en «Valores permitidos». */
export function DescargarPlantilla({ idCentro }: { idCentro?: string }): ReactNode {
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function descargar(): Promise<void> {
    setOcupado(true);
    setError(null);
    try {
      const f = await api.fichero(`/api/planes/plantilla${idCentro === undefined ? '' : `?idCentro=${idCentro}`}`);
      guardarFichero(f.blob, f.nombre);
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }
  return (
    <div className="nota info" style={{ marginBottom: 12 }}>
      <div>
        <strong>Plantilla oficial</strong>: hojas «Plan» (tareas) y «Ubicaciones» (las que el plan necesita: se reutilizan
        las que ya existen y se crean las nuevas al confirmar). La plantilla anterior (v1) se sigue aceptando, sin ubicaciones.
      </div>
      <button type="button" className="pequeno" style={{ marginTop: 8 }} disabled={ocupado} onClick={() => void descargar()}>
        {ocupado ? 'Preparando…' : `Descargar ${PLANTILLA_PLAN}`}
      </button>
      {error !== null && <ErrorCaja error={error} />}
    </div>
  );
}

const ACCION_UBICACION: Record<UbicacionImportacion['accion'], { texto: string; clase: string }> = {
  REUTILIZAR: { texto: 'Existente', clase: 'info' },
  CREAR: { texto: 'Se creará', clase: 'exito' },
  CONFLICTO: { texto: 'Conflicto', clase: 'aviso' },
  ERROR: { texto: 'Error', clase: 'error' },
};

/** Plantilla v2: qué pasa con cada ubicación de la hoja. Nada se ha creado todavía. */
function UbicacionesLote({ ubicaciones }: { ubicaciones: NonNullable<Preview['ubicaciones']> }): ReactNode {
  const t = ubicaciones.totales;
  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Ubicaciones</h2>
        <span className="suave pequena">todavía no se ha creado ninguna</span>
      </div>
      <div className="panel-cuerpo">
        <div className="cifras">
          <Cifra n={t.existentes} t="Existentes (se reutilizan)" />
          <Cifra n={t.aCrear} t="A crear al confirmar" />
          <Cifra n={t.conflictos} t="Conflictos" />
          <Cifra n={t.errores} t="Con error" />
        </div>
      </div>
      <div className="panel-cuerpo sin-relleno">
        <div className="tabla-desliza">
          <table>
            <thead>
              <tr>
                <th className="num">Fila</th>
                <th>Código</th>
                <th>Descripción</th>
                <th>Tipo</th>
                <th>Padre</th>
                <th>Qué se hará</th>
              </tr>
            </thead>
            <tbody>
              {ubicaciones.filas.map((u) => (
                <tr key={u.numeroFila}>
                  <td className="num">{u.numeroFila}</td>
                  <td className="cod">{u.codigo}</td>
                  <td>{u.descripcion ?? '—'}</td>
                  <td>{u.tipo ?? '—'}</td>
                  <td className="cod">{u.codigoPadre ?? '—'}</td>
                  <td>
                    <span className={`etiqueta ${ACCION_UBICACION[u.accion].clase}`}>{ACCION_UBICACION[u.accion].texto}</span>
                    {u.errores.map((e, i) => (
                      <div key={i} className="pequena">{e.detalle}</div>
                    ))}
                    {u.avisos.map((a, i) => (
                      <div key={`a${String(i)}`} className="pequena tenue">{a}</div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TablaErroresLote({ importacion }: { importacion: Importacion }): ReactNode {
  const errores = importacion.errores ?? [];
  if (errores.length === 0) return null;
  return (
    <div className="tabla-desliza sep">
      <table>
        <thead>
          <tr>
            <th className="num">Fila</th>
            <th>Referencia</th>
            <th>Ubicación</th>
            <th>Qué pasa</th>
          </tr>
        </thead>
        <tbody>
          {errores.map((e) => (
            <tr key={e.fila}>
              <td className="num">{e.fila}</td>
              <td className="cod">{e.referencia ?? '—'}</td>
              <td className="cod">{e.ubicacion ?? '—'}</td>
              <td>{e.detalle ?? e.codigo ?? e.estado}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const CLASE_DE: Record<string, string> = {
  ANADIDA: 'exito',
  MODIFICADA: 'info',
  RETIRADA: 'error',
  SIN_CAMBIOS: 'neutra',
  CONSERVADA_MANUAL: 'neutra',
};

function TablaTareasPreview({
  tareas,
  materialesAceptadas,
  alAlternarMaterial,
}: {
  tareas: TareaPreview[];
  materialesAceptadas: ReadonlySet<string>;
  alAlternarMaterial: (ref: string) => void;
}): ReactNode {
  const [verSinCambios, setVerSinCambios] = useState(false);
  const visibles = tareas.filter(
    (t) => verSinCambios || (t.clasificacion !== 'SIN_CAMBIOS' && t.clasificacion !== 'CONSERVADA_MANUAL'),
  );

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Tareas</h2>
        <label className="fila pequena suave" style={{ gap: 6 }}>
          <input
            type="checkbox"
            checked={verSinCambios}
            onChange={(e) => setVerSinCambios(e.target.checked)}
            style={{ width: 'auto' }}
          />
          Ver también las que no cambian
        </label>
      </div>
      <div className="panel-cuerpo sin-relleno">
        {visibles.length === 0 ? (
          <div className="estado-caja">
            <h3>Ninguna tarea cambia</h3>
            <p className="suave">La versión nueva sería idéntica a la vigente.</p>
          </div>
        ) : (
          <div className="tabla-desliza">
            <table>
              <thead>
                <tr>
                  <th>Referencia</th>
                  <th>Cambio</th>
                  <th>Ubicación</th>
                  <th>Tipo</th>
                  <th>Qué cambia</th>
                  <th>Aceptar</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((t) => (
                  <tr key={t.referenciaTarea}>
                    <td className="cod">{t.referenciaTarea}</td>
                    <td>
                      <span className={`etiqueta ${CLASE_DE[t.clasificacion] ?? 'neutra'}`}>
                        {t.clasificacion}
                      </span>
                    </td>
                    <td className="cod">{t.codigoUbicacion ?? '—'}</td>
                    <td className="cod">{t.codigoTipoLimpieza ?? '—'}</td>
                    <td>
                      {t.cambios.length === 0 ? (
                        <span className="tenue">—</span>
                      ) : (
                        <div className="apila">
                          {t.cambios.map((c, i) => (
                            <CambioLinea key={i} cambio={c} />
                          ))}
                        </div>
                      )}
                      {t.avisos.map((a, i) => (
                        <div key={i} className="pequena suave" style={{ marginTop: 4 }}>
                          {a.detalle}
                        </div>
                      ))}
                    </td>
                    <td>
                      {t.requiereConfirmacionExplicita ? (
                        <label className="fila pequena" style={{ gap: 6 }}>
                          <input
                            type="checkbox"
                            aria-label={`Aceptar el cambio material de ${t.referenciaTarea}`}
                            checked={materialesAceptadas.has(t.referenciaTarea)}
                            onChange={() => alAlternarMaterial(t.referenciaTarea)}
                            style={{ width: 'auto' }}
                          />
                          Acepto
                        </label>
                      ) : (
                        <span className="tenue">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Un cambio, campo a campo.
 *
 * MATERIAL y DESTACADO se distinguen con una etiqueta y con peso, no con
 * colores fuertes: en una lista de cincuenta cambios, el rojo por todas
 * partes deja de avisar de nada.
 */
function CambioLinea({ cambio }: { cambio: Cambio }): ReactNode {
  const antes = cambio.anteriorTexto ?? cambio.anterior ?? '—';
  const despues = cambio.nuevoTexto ?? cambio.nuevo ?? '—';
  return (
    <div className="pequena">
      {cambio.severidad !== 'NORMAL' && (
        <span
          className={`etiqueta ${cambio.severidad === 'MATERIAL' ? 'error' : 'aviso'}`}
          style={{ marginRight: 6 }}
        >
          {cambio.severidad}
        </span>
      )}
      <span className="suave">{cambio.campo}:</span> <span className="tenue">{antes}</span>{' '}
      <span className="tenue">→</span> <strong>{despues}</strong>
    </div>
  );
}

// ------------------------------------------------------------- publicado

function Publicado({
  resultado,
  alVerPlan,
  alGenerar,
}: {
  resultado: ResultadoConfirmacion;
  alVerPlan: () => void;
  alGenerar: () => void;
}): ReactNode {
  const c = resultado.commit;
  const i = resultado.impacto;

  return (
    <>
      <div className="nota exito">
        <strong>
          Plan {c.codigoPlan} v{c.numeroVersion} publicado correctamente.
        </strong>
      </div>

      <div className="panel">
        <div className="panel-cabecera">
          <h2>Resumen de la publicación</h2>
        </div>
        <div className="panel-cuerpo">
          <div className="cifras">
            <Cifra n={c.totalLineas} t="Tareas en la versión" />
            <Cifra n={c.anadidas} t="Añadidas" />
            <Cifra n={c.modificadas} t="Modificadas" />
            <Cifra n={c.retiradas} t="Retiradas" />
            <Cifra n={c.sinCambios} t="Sin cambios" />
            <Cifra n={c.conservadasManuales} t="Manuales conservadas" />
            <Cifra n={c.defaultsAsignacionCopiados} t="Defaults de asignación conservados" />
            <Cifra n={c.documentosAplicablesCopiados} t="Documentos aplicables conservados" />
            {(c.ubicacionesCreadas ?? 0) > 0 && <Cifra n={c.ubicacionesCreadas ?? 0} t="Ubicaciones creadas" />}
          </div>
          {/*
            ASSIGN-GEN-DEBT-02 (DEBT-ZERO-01): un default se omite —nunca
            se inventa— cuando el operario ya no está habilitado en el
            centro del plan. Sólo se muestra cuando pasa algo que de verdad
            conviene mirar; 0 omitidos es el caso normal y no necesita nota.
          */}
          {c.defaultsAsignacionOmitidos > 0 && (
            <div className="nota aviso">
              {c.defaultsAsignacionOmitidos} default
              {c.defaultsAsignacionOmitidos === 1 ? '' : 's'} de asignación no se ha
              {c.defaultsAsignacionOmitidos === 1 ? '' : 'n'} podido conservar: el operario ya
              no está habilitado en este centro. No se ha asignado nadie en su lugar —revisa
              las asignaciones de esta versión si hace falta cubrir esas tareas.
            </div>
          )}
        </div>
      </div>

      <div className="panel">
        <div className="panel-cabecera">
          <h2>Efecto sobre las órdenes ya emitidas</h2>
        </div>
        <div className="panel-cuerpo">
          {/*
            La versión nueva NUNCA modifica una OT ya emitida. Esto es un
            informe, no una acción, y por eso aquí no hay ningún botón que
            toque órdenes.
          */}
          {resultado.impactoError !== null ? (
            <div className="nota aviso">
              La versión se ha publicado correctamente, pero no se ha podido calcular el impacto
              sobre las órdenes ya emitidas: {resultado.impactoError}
            </div>
          ) : i === null ? (
            <p className="suave">
              Es la primera versión de este plan: no hay órdenes anteriores que puedan verse
              afectadas.
            </p>
          ) : (
            <>
              <p className="suave">
                Publicar una versión <strong>no modifica ninguna orden ya emitida</strong>. Esto es
                lo que habría que revisar.
              </p>
              <div className="cifras">
                <Cifra n={i.otsEvaluadas} t="OT evaluadas" />
                <Cifra n={i.otsImpactadas} t="OT impactadas" />
                <Cifra n={i.otsImpactadasCerradas} t="…de ellas cerradas" />
                <Cifra n={i.otsImpactadasAnuladas} t="…de ellas anuladas" />
                <Cifra n={i.tareasNuevasNoIncluidas} t="Trabajo nuevo no incluido" />
                <Cifra n={i.ocurrenciasQueYaNoSeProducirian} t="Ocurrencias que decaen" />
                <Cifra n={i.otsNuevasRequeridas} t="OT nuevas que harían falta" />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="fila">
        <button className="principal" onClick={alGenerar}>
          Generar órdenes de trabajo
        </button>
        <button onClick={alVerPlan}>Ver el plan</button>
      </div>
    </>
  );
}

function Cifra({ n, t }: { n: number; t: string }): ReactNode {
  return (
    <div className="cifra">
      <div className="n">{n}</div>
      <div className="t">{t}</div>
    </div>
  );
}

/** Mañana LOCAL (antes: mañana local convertida a UTC, que de madrugada era HOY). Ver `hoyLocal`, UAT 9.6-B1. */
function manana(): string {
  return hoyLocal(1);
}

// ------------------------------------------------- listado del histórico

export function PaginaImportaciones(): ReactNode {
  const { centro } = useCentro();
  const navegar = useNavigate();
  const estado = useDatos(
    () => api.get<{ importaciones: Importacion[] }>(`/api/centros/${centro.id}/importaciones`),
    [centro.id],
  );

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Importaciones</h2>
        <button
          className="principal pequeno"
          onClick={() => navegar(`/centros/${centro.id}/importaciones/nueva`)}
        >
          Nueva importación
        </button>
      </div>
      <div className="panel-cuerpo sin-relleno">
        {estado.cargando ? (
          <Cargando />
        ) : estado.error !== null ? (
          <ErrorCaja error={estado.error} reintentar={estado.recargar} />
        ) : (estado.datos?.importaciones ?? []).length === 0 ? (
          <div className="estado-caja">
            <h3>Todavía no se ha importado nada en este centro</h3>
            <p className="suave">
              El plan de limpieza entra desde la plantilla XLSX; el sistema enseña qué cambia antes
              de publicar nada.
            </p>
          </div>
        ) : (
          <div className="tabla-desliza">
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Plan</th>
                  <th>Estado</th>
                  <th className="num">Filas</th>
                  <th className="num">Con error</th>
                </tr>
              </thead>
              <tbody>
                {(estado.datos?.importaciones ?? []).map((imp) => (
                  <tr key={imp.id}>
                    <td>{fecha(imp.creadaEn.slice(0, 10))}</td>
                    <td className="cod">{imp.codigoPlan}</td>
                    <td>
                      <EstadoImp estado={imp.estado} />
                    </td>
                    <td className="num">{imp.filas}</td>
                    <td className="num">
                      {imp.filasConError > 0 ? (
                        <span className="etiqueta aviso">{imp.filasConError}</span>
                      ) : (
                        <span className="tenue">0</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function EstadoImp({ estado }: { estado: string }): ReactNode {
  const clase =
    estado === 'CONFIRMADA'
      ? 'exito'
      : estado === 'FALLIDA'
        ? 'error'
        : estado === 'DESCARTADA'
          ? 'neutra'
          : 'info';
  return <span className={`etiqueta ${clase}`}>{estado}</span>;
}

